import React, { useState, useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

import Navbar from '../../components/navbar/navbar'
import Footer from '../../components/footer';
import { API_BASE, getImageUrl } from '../../utils/api';

interface Agent {
    _id: string;
    avatar: string;
    name: string;
    title: string;
    phone: string;
    propertyCount: number;
}

export default function Agents() {
    const [agents, setAgents] = useState<Agent[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const location = useLocation();
    const tagParam = new URLSearchParams(location.search).get('tag');

    useEffect(() => {
        const fetchAgents = async () => {
            try {
                const response = await fetch(`${API_BASE}/users/agents`);
                const data = await response.json();
                if (Array.isArray(data)) {
                    setAgents(data);
                }
            } catch (error) {
                console.error('Error fetching agents:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchAgents();
    }, []);

    const filteredAgents = agents.filter(agent => {
        const matchesSearch = agent.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                             (agent.title && agent.title.toLowerCase().includes(searchTerm.toLowerCase()));
        
        let matchesTag = true;
        if (tagParam === 'Featured') {
            // Simplified logic: assume agents with more than 2 properties are "Featured"
            matchesTag = agent.propertyCount > 2;
        }

        return matchesSearch && matchesTag;
    });

    return (
        <>
            <Navbar transparent={false}/>

            <section className="bg-primary position-relative">
                <div className="position-absolute start-0 top-0 w-25 h-15 bg-light rounded-end-pill opacity-25 mt-4"></div>
                <div className="position-absolute start-0 bottom-0 w-15 h-20 bg-light rounded-top-pill opacity-25 ms-4"></div>
                <div className="position-absolute end-0 top-0 w-15 h-25 bg-light rounded-bottom-pill opacity-25 me-4"></div>
                <div className="position-absolute end-0 bottom-0 w-25 h-15 bg-light rounded-start-pill opacity-25 mb-4"></div>
                <div className="ht-30"></div>
                <div className="container">
                    <div className="row">
                        <div className="col-lg-12 col-md-12">
                            <h2 className="ipt-title text-light">{tagParam === 'Featured' ? 'Featured Agents' : 'All Agents'}</h2>
                            <span className="ipn-subtitle">Lists of our expert agents</span>
                        </div>
                    </div>
                </div>
                <div className="ht-30"></div>
            </section>

            <section className="gray-simple p-0">
                <div className="container">
                    <div className="row justify-content-center">
                        <div className="col-lg-10 col-md-12">
                            <div className="full-search-2 eclip-search italian-search hero-search-radius shadow-hard overlio-40">
                                <div className="hero-search-content">
                                    <div className="row">
                                        <div className="col-lg-10 col-md-9 col-sm-12">
                                            <div className="form-group">
                                                <div className="position-relative">
                                                    <input 
                                                        type="text" 
                                                        className="form-control border-0 ps-5" 
                                                        placeholder="Search agents by name or title..."
                                                        value={searchTerm}
                                                        onChange={(e) => setSearchTerm(e.target.value)}
                                                    />
                                                    <div className="position-absolute top-50 start-0 translate-middle-y ms-2">
                                                        <i className="fa-solid fa-magnifying-glass text-primary"></i>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                        <div className="col-lg-2 col-md-3 col-sm-12">
                                            <div className="form-group">
                                                <button className="btn btn-dark full-width">Search</button>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="gray-simple">
                <div className="container">
                    <div className="row justify-content-center g-4">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status">
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                            </div>
                        ) : filteredAgents.map((item, index) => (
                            <div className="col-xl-3 col-lg-4 col-md-6 col-sm-12" key={index}>
                                <div className="agents-grid card rounded-3 shadow border-0 overflow-hidden">
                                    <div className="agents-grid-wrap">
                                        <div className="fr-grid-thumb mx-auto text-center mt-5 mb-3">
                                            <Link to={`/agent-page/${item._id}`} className="d-inline-flex p-1 circle border">
                                                <img src={getImageUrl(item.avatar)} className="img-fluid circle" width="130" alt={item.name} onError={(e:any)=>e.target.src='/default-agent.png'} />
                                            </Link>
                                        </div>
                                        <div className="fr-grid-deatil text-center">
                                            <div className="fr-grid-deatil-flex px-3">
                                                <h5 className="fr-can-name mb-0"><Link to={`/agent-page/${item._id}`}>{item.name}</Link></h5>
                                                <span className="agent-property text-muted-2 text-sm">{item.title || 'Expert Agent'}</span>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="fr-grid-info d-flex align-items-center justify-content-between px-4 py-4">
                                        <div className="fr-grid-sder">
                                            <ul className="p-0 mb-0">
                                                <li><strong>Properties:</strong><span className="fw-medium text-primary ms-2">{item.propertyCount}</span></li>
                                                <li>
                                                    <div className="fr-can-rating">
                                                        <i className="fas fa-star fs-xs text-warning"></i>
                                                        <i className="fas fa-star fs-xs text-warning"></i>
                                                        <i className="fas fa-star fs-xs text-warning"></i>
                                                        <i className="fas fa-star fs-xs text-warning"></i>
                                                        <i className="fas fa-star fs-xs text-muted"></i>
                                                        <span className="reviews_text fs-sm text-muted-2 ms-1">(4.8)</span>
                                                    </div>
                                                </li>
                                            </ul>
                                        </div>
                                        <div className="fr-grid-deatil-flex-right">
                                            <Link to={`/agent-page/${item._id}`} className="square--50 rounded text-danger bg-light-danger transition-all hover:bg-danger hover:text-white">
                                                <i className="fa-solid fa-envelope"></i>
                                            </Link>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                    <div className="row">
                        <div className="col-lg-12 col-md-12 col-sm-12 text-center mt-5">
                            <Link to="/listings-list-with-sidebar" className="btn btn-primary px-lg-5 rounded">Explore More Agents</Link>
                        </div>
                    </div>
                </div>	
            </section>


        <Footer/>

    </>
  )
}
